import React, { useState } from 'react';

import { IndexGridNew } from '@rockerbox/styleguide';
import { Table, Label, Input, Icon } from 'semantic-ui-react';

import { Tooltip } from '../../../components';
import { formatCurrency, roundNumber, formatName } from '../../../utils/valueFormatter';
import { ChannelCell, CurrencyCell, NumberCell, labelStyles } from '../../MarketingMixModeling/parts/tableCells';
import { KPI_METRIC_TEXT, BASELINE_ROW_KEY } from '../constants';

export const SpendInputCell = ({ item, col }) => {
  const [showCustom, setShowCustom] = useState(false);
  const { key, allowCustomInput } = item;
  const { updateFunction, handleInput, featureStats, budgetPeriod } = col;

  const onChange = (e, { value }) => {
    if (value >= 0) {
      updateFunction(key, 'value', handleInput(value / budgetPeriod, featureStats?.[key].maxSpend));
    }
  };

  if (!key || key.includes(BASELINE_ROW_KEY)) {
    return (
      <Table.Cell>
        <p>{formatCurrency(item[col.key], 'USD', 0)}</p>
      </Table.Cell>
    );
  }

  return (
    <Table.Cell>
      <div style={{ display: 'inline-block', width: 100 }}>
        <Input
          compact
          type="number"
          step={50}
          min={0}
          max={featureStats?.[key]?.historicalSpendMax}
          icon="dollar"
          iconPosition="left"
          disabled={!allowCustomInput}
          value={parseInt(item[col.key])}
          onChange={onChange}
          className={`align-right slim-label ${!allowCustomInput && 'disabled-simple'}`}
          style={{ width: showCustom ? 89 : 67 }}
        />
        {!showCustom
        && (
        <Icon
          color="grey"
          name="pencil" onClick={() => {
            updateFunction(key, 'selection', 'custom');
            setShowCustom(true);
          }}
          style={{ cursor: 'pointer', marginTop: 8, marginLeft: 5 }}
        />
        )}
      </div>
    </Table.Cell>
  );
};

export const SpendTableHeader = ({ metricText, budgetPeriodName }) => {
  const TOOLTIP = `Our recommended ${budgetPeriodName} Spend values are based on our predictions to reach optimal ${metricText} efficiency, 
  but you can also keep your current spend or enter a custom value that achieves your goals.`;
  return (
    <>
      Proposed &nbsp;
      <Tooltip content={TOOLTIP} />
    </>
  );
};

export const TargetComparisonCell = ({ item }) => {
  const { targetValue, currentValue } = item;
  const percent = currentValue > 0 ? (targetValue - currentValue) / currentValue : 0;
  const noDifference = targetValue === currentValue || isNaN(percent);
  const positive = percent > 0;
  const positiveLabel = positive ? '+' : '';

  const content = `${positiveLabel}${roundNumber(percent * 100, 1)}%`;
  const color = noDifference ? '' : positive ? 'green' : 'red';

  return (
    <Table.Cell>
      <p>
        <Label
          content={content}
          color={color}
          style={labelStyles}
        />
      </p>
    </Table.Cell>
  );
};

export const MetricComparisonCell = isConversionKPI => ({ item, col }) => {
  const { formatter } = col;
  const value = item[col.key];

  if (Number.isNaN(value)) {
    return <Table.Cell>-</Table.Cell>;
  }

  const noDifference = value === 0 || Number.isNaN(value);
  const positive = isConversionKPI ? value < 0 : value > 0;
  const positiveLabel = isConversionKPI
    ? positive ? '' : '+'
    : positive ? '+' : '';

  return (
    <Table.Cell>
      <Label
        content={`${positiveLabel}${formatter(item[col.key])}`}
        color={noDifference ? '' : positive ? 'green' : 'red'}
        style={labelStyles}
      />
    </Table.Cell>
  );
};

export const KPIComparisonCell = ({ item, col }) => {
  const value = isNaN(item[col.key]) ? 0 : item[col.key];
  const noDifference = value === 0 || isNaN(value);
  const positive = value > 0;
  const positiveLabel = positive ? '+' : '';

  const content = `${positiveLabel}${roundNumber(value * 100, 1)}%`;
  const color = noDifference ? '' : positive ? 'green' : 'red';

  return (
    <Table.Cell>
      <p>
        <Label
          content={content}
          color={color}
          style={labelStyles}
        />
      </p>
    </Table.Cell>
  );
};

export const GroupedChannelTooltipContent = ({ data, isConversionKPI, projectionName }) => {
  const kpiText = isConversionKPI ? formatName(KPI_METRIC_TEXT.conversions) : formatName(KPI_METRIC_TEXT.revenue);
  const columns = [
    {
      display: 'Channel / Tactic',
      key: 'display',
      headerStyle: { textAlign: 'left' },
      as: ChannelCell,
    },
    {
      display: `Projected ${projectionName} Budget`,
      key: 'estimatedTotalValue',
      as: CurrencyCell,
    },
    {
      display: `Projected ${projectionName} ${kpiText}`,
      key: 'totalKPIAtTargetValue',
      as: isConversionKPI ? NumberCell : CurrencyCell,
    },
  ];
  return (
    <IndexGridNew cols={columns} data={data} />
  );
};
